import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Folder, FolderOpen, FileText, Eye, EyeOff, Lock, Unlock, Download, Loader2, ChevronDown, ChevronRight, UploadCloud, Check, Trash2, FilePlus2, X, ExternalLink } from 'lucide-react';
import { apiClient } from '../../api/client';

/**
 * DiligenceLibrary — Sprint 172 Part 1A.
 *
 * Founder-facing folder tree mounted at the top of the Data Room tab.
 * Renders the canonical 4-folder diligence library with per-file
 * visibility + lock toggles plus live download/dwell badges fed by the
 * /api/vault/diligence/tree endpoint.
 *
 * Cream cards (#FAFAED) + deep-plum text per the Sprint 172 design spec.
 */

const PALETTE = {
  cream: '#FAFAED',
  plum: '#2A2235',
  muted: '#5C526A',
  coral: '#F99B7D',
  mint: '#A3E2C9',
  lavender: '#9BB8ED',
  border: 'rgba(92,82,106,0.10)',
  accentSoft: '#FFF6EE',
};

const fmtSecs = (s) => {
  s = Number(s || 0);
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  return `${(s / 3600).toFixed(1)}h`;
};

const fmtTs = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const diffMin = Math.max(0, Math.floor((Date.now() - d.getTime()) / 60000));
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffMin < 1440) return `${Math.floor(diffMin / 60)}h ago`;
    return `${Math.floor(diffMin / 1440)}d ago`;
  } catch { return ''; }
};

export const DiligenceLibrary = () => {
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({}); // folder_id -> bool
  const [busy, setBusy] = useState({}); // file_id|folder_id -> bool
  const [error, setError] = useState(null);
  const [investorView, setInvestorView] = useState(false); // preview the room as investors see it
  const [liveRoomBusy, setLiveRoomBusy] = useState(false);

  const openLiveRoom = async () => {
    if (liveRoomBusy) return;
    setLiveRoomBusy(true);
    setError(null);
    try {
      const res = await apiClient.request('/api/vault/datarooms/founder-preview', { method: 'POST' });
      localStorage.setItem('hibff_founder_preview_code', res.passcode);
      window.open('/dataroom', '_blank', 'noopener');
    } catch (e) { setError(friendlyError(e, 'Could not open the live investor room')); }
    setLiveRoomBusy(false);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.request('/api/vault/diligence/tree');
      setTree(res);
      // Default-expand all folders so the founder sees the full tree at once.
      setExpanded(prev => {
        const next = { ...prev };
        (res.tree || []).forEach(f => { if (next[f.folder_id] === undefined) next[f.folder_id] = true; });
        return next;
      });
    } catch (e) {
      setError(e.message || 'Failed to load diligence library');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // A 401 from any vault endpoint means the vault session auto-locked.
  // Surface a human message and flip the whole vault back to the TOTP gate.
  const friendlyError = (e, fallback) => {
    const msg = (e && e.message) || '';
    if ((e && e.status === 401) || /\b401\b/.test(msg) || /expired/i.test(msg)) {
      window.dispatchEvent(new CustomEvent('hibff:vault:expired'));
      return 'Vault session expired — enter your authenticator code to unlock again.';
    }
    return msg || fallback;
  };

  const setFolderFlag = async (folder, field, value) => {
    setBusy(b => ({ ...b, [folder.folder_id]: true }));
    try {
      await apiClient.request(`/api/vault/diligence/folders/${folder.folder_id}`, {
        method: 'PUT',
        body: JSON.stringify({ [field]: value }),
      });
      await load();
    } catch (e) { setError(friendlyError(e, 'Update failed')); }
    setBusy(b => ({ ...b, [folder.folder_id]: false }));
  };

  const setFileFlag = async (file, field, value) => {
    setBusy(b => ({ ...b, [file.file_id]: true }));
    try {
      await apiClient.request(`/api/vault/diligence/files/${file.file_id}`, {
        method: 'PUT',
        body: JSON.stringify({ [field]: value }),
      });
      await load();
    } catch (e) { setError(friendlyError(e, 'Update failed')); }
    setBusy(b => ({ ...b, [file.file_id]: false }));
  };

  const downloadFile = async (file) => {
    setBusy(b => ({ ...b, [`dl-${file.file_id}`]: true }));
    try {
      const url = `${apiClient.baseUrl}/api/vault/diligence/files/${file.file_id}/download`;
      const token = apiClient.getToken();
      const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) { const err = new Error(`HTTP ${res.status}`); err.status = res.status; throw err; }
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = (file.label || file.file_id).replace(/\s+/g, '-');
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objUrl), 30000);
      await load();
    } catch (e) { setError(friendlyError(e, 'Download failed')); }
    setBusy(b => ({ ...b, [`dl-${file.file_id}`]: false }));
  };

  // Sprint 178 — upload / delete native-PDF override.
  const uploadOverride = async (file, blob) => {
    setBusy(b => ({ ...b, [`up-${file.file_id}`]: true }));
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', blob, blob.name || `${file.file_id}.pdf`);
      const url = `${apiClient.baseUrl}/api/vault/diligence/files/${file.file_id}/upload`;
      const token = apiClient.getToken();
      const res = await fetch(url, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) { const err = new Error(body.detail || `HTTP ${res.status}`); err.status = res.status; throw err; }
      await load();
    } catch (e) { setError(friendlyError(e, 'Upload failed')); }
    setBusy(b => ({ ...b, [`up-${file.file_id}`]: false }));
  };

  const deleteListing = async (file) => {
    const msg = `Delete the listing "${file.label}" from the Data Room? Investors will no longer see it.`;
    if (!window.confirm(msg)) return;
    setBusy(b => ({ ...b, [file.file_id]: true }));
    setError(null);
    try {
      await apiClient.request(`/api/vault/diligence/files/${file.file_id}`, { method: 'DELETE' });
      await load();
    } catch (e) { setError(friendlyError(e, 'Delete failed')); }
    setBusy(b => ({ ...b, [file.file_id]: false }));
  };

  // Attach additional PDFs to a listing — investors receive one merged PDF.
  const addAttachments = async (file, fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setBusy(b => ({ ...b, [`att-${file.file_id}`]: true }));
    setError(null);
    try {
      const token = apiClient.getToken();
      for (const f of files) {
        const fd = new FormData();
        fd.append('file', f, f.name);
        const res = await fetch(`${apiClient.baseUrl}/api/vault/diligence/files/${file.file_id}/attachments`, {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: fd,
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) { const err = new Error(`${f.name}: ${body.detail || `HTTP ${res.status}`}`); err.status = res.status; throw err; }
      }
      await load();
    } catch (e) { setError(friendlyError(e, 'Attachment upload failed')); }
    setBusy(b => ({ ...b, [`att-${file.file_id}`]: false }));
  };

  const deleteAttachment = async (file, att) => {
    if (!window.confirm(`Remove "${att.label}" from "${file.label}"?`)) return;
    setBusy(b => ({ ...b, [`att-${file.file_id}`]: true }));
    setError(null);
    try {
      await apiClient.request(`/api/vault/diligence/files/${file.file_id}/attachments/${att.attachment_id}`, { method: 'DELETE' });
      await load();
    } catch (e) { setError(friendlyError(e, 'Attachment removal failed')); }
    setBusy(b => ({ ...b, [`att-${file.file_id}`]: false }));
  };

  // Multi-doc upload — adds NEW documents to a folder, one POST per PDF.
  const addFolderDocs = async (folder, fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setBusy(b => ({ ...b, [`add-${folder.folder_id}`]: true }));
    setError(null);
    try {
      const token = apiClient.getToken();
      for (const f of files) {
        const fd = new FormData();
        fd.append('file', f, f.name);
        const res = await fetch(`${apiClient.baseUrl}/api/vault/diligence/folders/${folder.folder_id}/files`, {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: fd,
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) { const err = new Error(`${f.name}: ${body.detail || `HTTP ${res.status}`}`); err.status = res.status; throw err; }
      }
      await load();
    } catch (e) { setError(friendlyError(e, 'Upload failed')); }
    setBusy(b => ({ ...b, [`add-${folder.folder_id}`]: false }));
  };

  if (loading && !tree) {
    return (
      <div className="rounded-2xl p-5 text-sm" style={{ background: PALETTE.cream, border: `1px solid ${PALETTE.border}`, color: PALETTE.muted }} data-testid="diligence-loading">
        <Loader2 size={14} className="inline animate-spin mr-2" /> Loading diligence library…
      </div>
    );
  }

  return (
    <div className="rounded-2xl p-5 lg:p-6" style={{ background: PALETTE.cream, border: `1px solid ${PALETTE.border}`, borderLeft: `4px solid ${PALETTE.coral}` }} data-testid="diligence-library">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold mb-1" style={{ color: '#9A4A33' }}>Diligence Library</div>
          <h3 className="text-lg font-black" style={{ color: PALETTE.plum }}>Secure file tree</h3>
          <p className="text-xs mt-0.5" style={{ color: PALETTE.muted }}>
            {investorView
              ? 'Exactly what an investor sees in their Data Room — greyed items are NOT visible to them and show why.'
              : 'Toggle visibility or lock individual files / entire folders.'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={openLiveRoom}
            disabled={liveRoomBusy}
            className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-full transition-transform hover:scale-105 disabled:opacity-60"
            style={{ background: '#1A1209', color: '#F5C96B', border: '1px solid #3A2E18' }}
            title="Open the real passcode-protected investor Data Room in a new tab — exactly what investors see"
            data-testid="open-live-room-btn"
          >
            {liveRoomBusy ? <Loader2 size={13} className="animate-spin" /> : <ExternalLink size={13} />} Open Live Room
          </button>
          <button
            type="button"
            onClick={() => setInvestorView(v => !v)}
            className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-full transition-transform hover:scale-105"
            style={investorView
              ? { background: PALETTE.plum, color: '#fff' }
              : { background: '#EEF4FF', color: '#3F4D7A', border: '1px solid #C9D6F2' }}
            title={investorView ? 'Back to managing documents' : 'Preview the Data Room exactly as an investor sees it'}
            data-testid="investor-view-toggle"
          >
            <Eye size={13} /> {investorView ? 'Back to Manage' : 'Investor View'}
          </button>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: PALETTE.muted }}>{tree?.folder_count || 0} folders</div>
            <div className="text-xs font-bold" style={{ color: PALETTE.plum }}>{tree?.file_count || 0} files</div>
          </div>
        </div>
      </div>

      {error && <div className="mb-3 px-3 py-2 rounded-lg text-xs" style={{ background: '#F2E4E4', color: '#7A4040' }} data-testid="diligence-error">{error}</div>}

      {investorView && <InvestorPreview tree={tree} />}

      {!investorView && (
      <div className="space-y-3" data-testid="diligence-tree">
        {(tree?.tree || []).map(folder => {
          const open = expanded[folder.folder_id] !== false;
          const folderBusy = !!busy[folder.folder_id];
          return (
            <div key={folder.folder_id} className="rounded-xl overflow-hidden" style={{ background: '#fff', border: `1px solid ${PALETTE.border}` }} data-testid={`diligence-folder-${folder.folder_id}`}>
              <div className="flex items-center justify-between gap-3 px-4 py-3" style={{ background: folder.visible ? PALETTE.accentSoft : '#F3F0F7' }}>
                <button
                  type="button"
                  onClick={() => setExpanded(e => ({ ...e, [folder.folder_id]: !open }))}
                  className="flex items-center gap-2 min-w-0 text-left"
                  data-testid={`diligence-folder-toggle-${folder.folder_id}`}
                >
                  {open ? <ChevronDown size={14} style={{ color: PALETTE.muted }} /> : <ChevronRight size={14} style={{ color: PALETTE.muted }} />}
                  {open ? <FolderOpen size={16} style={{ color: '#9A4A33' }} /> : <Folder size={16} style={{ color: '#9A4A33' }} />}
                  <div className="min-w-0">
                    <div className="text-sm font-black truncate" style={{ color: PALETTE.plum }}>{folder.label}</div>
                    {folder.description && <div className="text-[11px] truncate" style={{ color: PALETTE.muted }}>{folder.description}</div>}
                  </div>
                </button>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <FolderAddDocs
                    folder={folder}
                    busy={!!busy[`add-${folder.folder_id}`]}
                    onAdd={(list) => addFolderDocs(folder, list)}
                  />
                  <ToggleButton
                    active={!!folder.visible}
                    activeIcon={Eye}
                    inactiveIcon={EyeOff}
                    activeLabel="Visible"
                    inactiveLabel="Hidden"
                    activeBg={PALETTE.mint}
                    inactiveBg="#E5E1EC"
                    disabled={folderBusy}
                    title={folder.visible ? 'Folder visible to investors — click to hide the whole folder' : 'Folder hidden from investors — click to show it'}
                    onClick={() => setFolderFlag(folder, 'visible', !folder.visible)}
                    testId={`folder-visible-${folder.folder_id}`}
                  />
                  <ToggleButton
                    active={!!folder.locked}
                    activeIcon={Lock}
                    inactiveIcon={Unlock}
                    activeLabel="Locked"
                    inactiveLabel="Open"
                    activeBg="#F2E4E4"
                    inactiveBg="#F0F4FB"
                    activeColor="#7A4040"
                    inactiveColor="#3F4D7A"
                    disabled={folderBusy}
                    title={folder.locked ? 'Folder locked — investors cannot download anything inside. Click to unlock' : 'Folder open — investor downloads allowed. Click to lock the whole folder'}
                    onClick={() => setFolderFlag(folder, 'locked', !folder.locked)}
                    testId={`folder-lock-${folder.folder_id}`}
                  />
                </div>
              </div>

              {open && (
                <ul className="divide-y" style={{ borderColor: PALETTE.border }} data-testid={`diligence-files-${folder.folder_id}`}>
                  {(folder.files || []).length === 0 && (
                    <li className="px-4 py-3 text-xs" style={{ color: PALETTE.muted }}>No files in this folder yet.</li>
                  )}
                  {(folder.files || []).map(file => {
                    const fileBusy = !!busy[file.file_id];
                    return (
                      <li key={file.file_id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3" data-testid={`diligence-file-${file.file_id}`}>
                        <div className="flex items-start gap-2 min-w-0 flex-1" style={{ flexBasis: '260px' }}>
                          <FileText size={13} style={{ color: PALETTE.muted, flexShrink: 0, marginTop: 3 }} />
                          <div className="min-w-0">
                            <div className="text-sm font-semibold break-words" style={{ color: PALETTE.plum }}>{file.label}
                              {file.pending_upload && !file.override_active && (
                                <span className="ml-2 inline-flex items-center text-[9px] font-bold px-1.5 py-0.5 rounded-full align-middle" style={{ background: '#FEF3C7', color: '#92400E' }} data-testid={`diligence-pending-${file.file_id}`}>Awaiting upload</span>
                              )}
                            </div>
                            {file.description && <div className="text-[11px] leading-snug break-words" style={{ color: PALETTE.muted }}>{file.description}</div>}
                            {(file.attachments || []).length > 0 && (
                              <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid={`diligence-attachments-${file.file_id}`}>
                                {file.attachments.map(att => (
                                  <span key={att.attachment_id} className="inline-flex items-center gap-1 text-[10px] font-semibold pl-2 pr-1 py-0.5 rounded-full" style={{ background: '#EEF4FF', color: '#3F4D7A' }} data-testid={`diligence-att-${att.attachment_id}`}>
                                    <FileText size={9} /> {att.label}
                                    <button
                                      type="button"
                                      onClick={() => deleteAttachment(file, att)}
                                      className="rounded-full p-0.5 hover:bg-red-100"
                                      title={`Remove "${att.label}" from this listing`}
                                      data-testid={`diligence-att-del-${att.attachment_id}`}
                                    >
                                      <X size={10} style={{ color: '#7A4040' }} />
                                    </button>
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0 ml-auto justify-end">
                          <ToggleButton
                            active={!!file.visible}
                            activeIcon={Eye}
                            inactiveIcon={EyeOff}
                            activeLabel=""
                            inactiveLabel=""
                            activeBg={PALETTE.mint}
                            inactiveBg="#E5E1EC"
                            small
                            disabled={fileBusy}
                            title={file.visible ? 'Visible to investors — click to hide this file' : 'Hidden from investors — click to make it visible'}
                            onClick={() => setFileFlag(file, 'visible', !file.visible)}
                            testId={`file-visible-${file.file_id}`}
                          />
                          <ToggleButton
                            active={!!file.locked}
                            activeIcon={Lock}
                            inactiveIcon={Unlock}
                            activeLabel=""
                            inactiveLabel=""
                            activeBg="#F2E4E4"
                            inactiveBg="#F0F4FB"
                            activeColor="#7A4040"
                            inactiveColor="#3F4D7A"
                            small
                            disabled={fileBusy}
                            title={file.locked ? 'Locked — investors cannot download. Click to unlock' : 'Open — investors can download. Click to lock'}
                            onClick={() => setFileFlag(file, 'locked', !file.locked)}
                            testId={`file-lock-${file.file_id}`}
                          />
                          <span className="w-px h-5 flex-shrink-0" style={{ background: PALETTE.border }} aria-hidden="true" />
                          <RowAddAttachment
                            file={file}
                            busy={!!busy[`att-${file.file_id}`]}
                            onAdd={(list) => addAttachments(file, list)}
                          />
                          <UploadOverrideButton
                            file={file}
                            busy={!!busy[`up-${file.file_id}`]}
                            onUpload={(blob) => uploadOverride(file, blob)}
                          />
                          <button
                            onClick={() => deleteListing(file)}
                            disabled={fileBusy}
                            className="inline-flex items-center px-2 py-1.5 rounded-full disabled:opacity-50 transition-transform hover:scale-110"
                            style={{ background: '#F2E4E4', color: '#7A4040' }}
                            title="Delete this listing — removes it from the Data Room"
                            data-testid={`diligence-delete-${file.file_id}`}
                          >
                            <Trash2 size={13} />
                          </button>
                          <button
                            onClick={() => downloadFile(file)}
                            disabled={!!busy[`dl-${file.file_id}`]}
                            className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1.5 rounded-full disabled:opacity-50"
                            style={{ background: PALETTE.coral, color: '#fff' }}
                            title="Download this document — exactly what an investor receives"
                            data-testid={`diligence-download-${file.file_id}`}
                          >
                            <Download size={12} /> {busy[`dl-${file.file_id}`] ? '…' : 'PDF'}
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
};

// ── Investor View — faithful replica of GET /api/dataroom/documents rules:
//    a file appears to investors only when visible && !locked && !pending_upload.
//    Everything else is shown greyed with the reason, so gaps are obvious.
const investorCanSee = (f) => !!f.visible && !f.locked && !f.pending_upload;

const InvestorPreview = ({ tree }) => {
  const folders = tree?.tree || [];
  const allFiles = folders.flatMap(f => f.files || []);
  const visible = allFiles.filter(investorCanSee);
  const awaiting = allFiles.filter(f => f.pending_upload && !investorCanSee(f));
  const hidden = allFiles.filter(f => !f.visible && !f.pending_upload);
  const locked = allFiles.filter(f => f.visible && f.locked && !f.pending_upload);
  const reasonFor = (f) => f.pending_upload
    ? { label: 'Awaiting upload', bg: '#FEF3C7', color: '#92400E' }
    : !f.visible
      ? { label: 'Hidden by you', bg: '#E5E1EC', color: '#5C526A' }
      : { label: 'Locked', bg: '#F2E4E4', color: '#7A4040' };
  return (
    <div data-testid="investor-preview">
      <div className="flex flex-wrap gap-2 mb-4" data-testid="investor-preview-summary">
        <span className="inline-flex items-center gap-1 text-[11px] font-bold px-3 py-1.5 rounded-full" style={{ background: '#DCF5E5', color: '#1F5C44' }} data-testid="investor-preview-count-visible">
          <Eye size={12} /> {visible.length} visible to investors
        </span>
        <span className="inline-flex items-center gap-1 text-[11px] font-bold px-3 py-1.5 rounded-full" style={{ background: '#FEF3C7', color: '#92400E' }} data-testid="investor-preview-count-awaiting">
          <UploadCloud size={12} /> {awaiting.length} awaiting upload
        </span>
        <span className="inline-flex items-center gap-1 text-[11px] font-bold px-3 py-1.5 rounded-full" style={{ background: '#E5E1EC', color: '#5C526A' }} data-testid="investor-preview-count-hidden">
          <EyeOff size={12} /> {hidden.length} hidden
        </span>
        <span className="inline-flex items-center gap-1 text-[11px] font-bold px-3 py-1.5 rounded-full" style={{ background: '#F2E4E4', color: '#7A4040' }} data-testid="investor-preview-count-locked">
          <Lock size={12} /> {locked.length} locked
        </span>
      </div>
      <div className="space-y-3">
        {folders.map(folder => {
          const files = folder.files || [];
          const seen = files.filter(investorCanSee);
          const gaps = files.filter(f => !investorCanSee(f));
          return (
            <div key={folder.folder_id} className="rounded-xl overflow-hidden" style={{ background: '#fff', border: `1px solid ${PALETTE.border}` }} data-testid={`investor-preview-folder-${folder.folder_id}`}>
              <div className="flex items-center justify-between gap-3 px-4 py-3" style={{ background: PALETTE.accentSoft }}>
                <div className="flex items-center gap-2 min-w-0">
                  <FolderOpen size={16} style={{ color: '#9A4A33' }} />
                  <div className="min-w-0">
                    <div className="text-sm font-black" style={{ color: PALETTE.plum }}>{folder.label}</div>
                    {folder.description && <div className="text-[11px]" style={{ color: PALETTE.muted }}>{folder.description}</div>}
                  </div>
                </div>
                <span className="flex-shrink-0 text-[11px] font-bold px-2.5 py-1 rounded-full whitespace-nowrap"
                  style={seen.length === files.length
                    ? { background: '#DCF5E5', color: '#1F5C44' }
                    : { background: '#FEF3C7', color: '#92400E' }}
                  data-testid={`investor-preview-folder-count-${folder.folder_id}`}>
                  {seen.length} of {files.length} visible
                </span>
              </div>
              <ul className="divide-y" style={{ borderColor: PALETTE.border }}>
                {files.length === 0 && (
                  <li className="px-4 py-3 text-xs italic" style={{ color: PALETTE.muted }}>Investors see this section with a &quot;documents being compiled&quot; note.</li>
                )}
                {seen.map(f => (
                  <li key={f.file_id} className="flex items-start gap-2 px-4 py-2.5" data-testid={`investor-sees-${f.file_id}`}>
                    <FileText size={13} style={{ color: '#1F5C44', flexShrink: 0, marginTop: 3 }} />
                    <div className="min-w-0">
                      <div className="text-sm font-semibold break-words" style={{ color: PALETTE.plum }}>{f.label}
                        {(f.attachments || []).length > 0 && (
                          <span className="ml-2 text-[10px] font-bold align-middle" style={{ color: '#3F4D7A' }}>+{f.attachments.length} attached</span>
                        )}
                      </div>
                      {f.description && <div className="text-[11px] leading-snug break-words" style={{ color: PALETTE.muted }}>{f.description}</div>}
                    </div>
                  </li>
                ))}
                {gaps.map(f => {
                  const r = reasonFor(f);
                  return (
                    <li key={f.file_id} className="flex items-start gap-2 px-4 py-2.5" style={{ opacity: 0.55, background: '#FAFAF7' }} data-testid={`investor-gap-${f.file_id}`}>
                      <FileText size={13} style={{ color: PALETTE.muted, flexShrink: 0, marginTop: 3 }} />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold break-words" style={{ color: PALETTE.muted, textDecoration: 'line-through' }}>{f.label}</div>
                      </div>
                      <span className="flex-shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap" style={{ background: r.bg, color: r.color }}>{r.label}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ToggleButton = ({ active, activeIcon: AIcon, inactiveIcon: IIcon, activeLabel, inactiveLabel, activeBg, inactiveBg, activeColor = '#1F5C44', inactiveColor = '#5C526A', small, disabled, onClick, testId, title }) => {
  const Icon = active ? AIcon : IIcon;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title || (active ? activeLabel : inactiveLabel)}
      className={`inline-flex items-center gap-1 ${small ? 'px-2 py-1.5' : 'px-2.5 py-1.5'} rounded-full text-[10px] font-bold disabled:opacity-50 transition-transform hover:scale-110`}
      style={{ background: active ? activeBg : inactiveBg, color: active ? activeColor : inactiveColor }}
      data-testid={testId}
    >
      <Icon size={small ? 14 : 13} />
      {!small && <span>{active ? activeLabel : inactiveLabel}</span>}
    </button>
  );
};

export default DiligenceLibrary;

// Per-listing "Add" — attach additional PDFs to one listing. Investors
// receive the base document with all attachments merged on download.
const RowAddAttachment = ({ file, busy, onAdd }) => {
  const inputRef = useRef(null);
  const handlePick = (e) => {
    if (e.target.files && e.target.files.length) onAdd(e.target.files);
    if (inputRef.current) inputRef.current.value = '';
  };
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,application/pdf"
        multiple
        style={{ display: 'none' }}
        onChange={handlePick}
        data-testid={`row-add-input-${file.file_id}`}
      />
      <button
        type="button"
        onClick={() => inputRef.current && inputRef.current.click()}
        disabled={busy}
        className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1.5 rounded-full disabled:opacity-50 transition-transform hover:scale-110"
        style={{ background: '#EEF4FF', color: '#3F4D7A' }}
        title="Add more PDFs to this listing — they are appended to the document investors download"
        data-testid={`row-add-docs-${file.file_id}`}
      >
        {busy ? <Loader2 size={12} className="animate-spin" /> : <FilePlus2 size={12} />} Add
      </button>
    </>
  );
};

// Folder-level multi-document uploader — pick several PDFs at once; each
// becomes a new document row in the folder, live to investors immediately.
const FolderAddDocs = ({ folder, busy, onAdd }) => {
  const inputRef = useRef(null);
  const handlePick = (e) => {
    if (e.target.files && e.target.files.length) onAdd(e.target.files);
    if (inputRef.current) inputRef.current.value = '';
  };
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,application/pdf"
        multiple
        style={{ display: 'none' }}
        onChange={handlePick}
        data-testid={`folder-add-input-${folder.folder_id}`}
      />
      <button
        type="button"
        onClick={() => inputRef.current && inputRef.current.click()}
        disabled={busy}
        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[10px] font-bold disabled:opacity-50 transition-transform hover:scale-110"
        style={{ background: '#FFE3D6', color: '#9A4A33' }}
        title="Add documents to this folder — select one or more PDFs"
        data-testid={`folder-add-docs-${folder.folder_id}`}
      >
        {busy ? <Loader2 size={13} className="animate-spin" /> : <FilePlus2 size={13} />}
        <span>Add docs</span>
      </button>
    </>
  );
};

// Sprint 178 — Native-PDF override upload/delete control.
// When `file.override_active` is false, shows a soft-coral upload-cloud
// icon that triggers a hidden <input type="file"> filter to PDFs.
// When `file.override_active` is true, shows a green checkmark with a
// secondary trash button to revert to the ReportLab-compiled brief.
const UploadOverrideButton = ({ file, busy, onUpload }) => {
  const inputRef = useRef(null);
  const active = !!file.override_active;
  const handlePick = (e) => {
    const f = e.target.files && e.target.files[0];
    if (f) onUpload(f);
    if (inputRef.current) inputRef.current.value = '';
  };
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,application/pdf"
        style={{ display: 'none' }}
        onChange={handlePick}
        data-testid={`upload-input-${file.file_id}`}
      />
      {active ? (
        <span className="inline-flex items-center gap-1" data-testid={`upload-active-${file.file_id}`}>
          <span
            className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1.5 rounded-full"
            style={{ background: '#DCF5E5', color: '#1F5C44' }}
            title="Your uploaded PDF is live — investors see this exact file"
          >
            <Check size={12} /> Native
          </span>
          <button
            type="button"
            onClick={() => inputRef.current && inputRef.current.click()}
            disabled={busy}
            className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1.5 rounded-full disabled:opacity-50 transition-transform hover:scale-110"
            style={{ background: '#FFE3D6', color: '#9A4A33' }}
            title="Replace this document — pick a new PDF to swap in immediately"
            data-testid={`upload-replace-${file.file_id}`}
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <UploadCloud size={12} />} Replace
          </button>
        </span>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current && inputRef.current.click()}
          disabled={busy}
          className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1.5 rounded-full disabled:opacity-50 transition-transform hover:scale-110"
          style={{ background: '#FFE3D6', color: '#9A4A33' }}
          title="Upload the official PDF for this slot — replaces the auto-compiled brief"
          data-testid={`upload-trigger-${file.file_id}`}
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <UploadCloud size={12} />} Upload PDF
        </button>
      )}
    </>
  );
};
